/**
 * SpinScreen - Vegas Classic Slot Machine
 * Random member selection with animated lever
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import { useAudio } from '../hooks/useAudio';
import { getInitials, getAvatarColor } from '../utils';
import type { SquadMember } from '../types';
import './SpinScreen.css';

type SpinPhase = 'READY' | 'SPINNING' | 'STOPPING' | 'RESULT';
type LeverState = 'idle' | 'pulled' | 'returning';

export function SpinScreen() {
  const { state, actions } = useGame();
  const { remainingMembers, currentMember } = state;
  const { play, stop, stopAll } = useAudio();

  const [phase, setPhase] = useState<SpinPhase>(state.phase === 'SPIN_RESULT' ? 'RESULT' : 'READY');
  const [reelStates, setReelStates] = useState<[number, number, number]>([0, 0, 0]);
  const [selectedMember, setSelectedMember] = useState<SquadMember | null>(currentMember);
  const [leverState, setLeverState] = useState<LeverState>('idle');
  const [showCoins, setShowCoins] = useState(false);

  // Select a random member
  const selectRandomMember = useCallback((): SquadMember | null => {
    if (remainingMembers.length === 0) return null;
    const index = Math.floor(Math.random() * remainingMembers.length);
    return remainingMembers[index];
  }, [remainingMembers]);

  // Handle spin animation
  const handleSpin = useCallback(() => {
    if (phase !== 'READY' || remainingMembers.length === 0) return;

    // Pull the lever
    setLeverState('pulled');

    // Start spinning after lever is pulled
    setTimeout(() => {
      setPhase('SPINNING');
      setLeverState('returning');

      const member = selectRandomMember();
      if (!member) return;

      // Simulate spinning reels
      const spinDuration = 2500;
      const stopDelay = 400;

      // Rapid cycling during spin
      const spinInterval = setInterval(() => {
        setReelStates([
          Math.floor(Math.random() * remainingMembers.length),
          Math.floor(Math.random() * remainingMembers.length),
          Math.floor(Math.random() * remainingMembers.length),
        ]);
      }, 80);

      // Stop reels one by one
      const memberIndex = remainingMembers.findIndex((m) => m.accountId === member.accountId);

      setTimeout(() => {
        setPhase('STOPPING');
        setReelStates((prev) => [memberIndex, prev[1], prev[2]]);
      }, spinDuration);

      setTimeout(() => {
        setReelStates((prev) => [prev[0], memberIndex, prev[2]]);
      }, spinDuration + stopDelay);

      setTimeout(() => {
        clearInterval(spinInterval);
        setReelStates([memberIndex, memberIndex, memberIndex]);
        setSelectedMember(member);
        setPhase('RESULT');
        setShowCoins(true);
        actions.selectMember(member);
        actions.setPhase('SPIN_RESULT');

        // Hide coins after animation
        setTimeout(() => setShowCoins(false), 2000);
      }, spinDuration + stopDelay * 2);
    }, 300);
  }, [phase, remainingMembers, selectRandomMember, actions]);

  // Handle lever click
  const handleLeverClick = () => {
    if (phase === 'READY' && leverState === 'idle') {
      handleSpin();
    }
  };

  // Handle result actions
  const handleLetsGo = () => {
    if (selectedMember) {
      actions.setPhase('PRESENTATION');
    }
  };

  const handleAbsent = () => {
    if (selectedMember) {
      actions.markAbsent(selectedMember);
      setSelectedMember(null);
      setPhase('READY');
      setLeverState('idle');
      actions.setPhase('SPIN');
    }
  };

  const handleLate = () => {
    if (selectedMember) {
      actions.markLate(selectedMember);
      setSelectedMember(null);
      setPhase('READY');
      setLeverState('idle');
      actions.setPhase('SPIN');
    }
  };

  // End daily and go to recap
  const handleEndDaily = () => {
    actions.setPhase('RECAP');
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // End daily with Escape or Tab
      if (e.code === 'Escape' || e.code === 'Tab') {
        e.preventDefault();
        handleEndDaily();
        return;
      }

      if (phase === 'READY') {
        if (e.code === 'Space' || e.code === 'Enter') {
          handleSpin();
        }
      } else if (phase === 'RESULT') {
        if (e.code === 'Enter') {
          handleLetsGo();
        } else if (e.code === 'KeyX') {
          handleAbsent();
        } else if (e.code === 'KeyY') {
          handleLate();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, handleSpin]);

  // Sound effects based on spin phase
  useEffect(() => {
    if (phase === 'SPINNING') {
      play('slot-spin', { loop: true, volume: 0.4 });
    } else if (phase === 'RESULT') {
      stop('slot-spin');
      play('slot-reveal', { volume: 0.6 });
    }

    return () => {
      // Cleanup when phase changes or component unmounts
      if (phase === 'SPINNING') {
        stop('slot-spin');
      }
    };
  }, [phase, play, stop]);

  // Stop all sounds when leaving the screen
  useEffect(() => {
    return () => stopAll();
  }, [stopAll]);

  // Check if all members are done
  useEffect(() => {
    if (remainingMembers.length === 0 && phase === 'READY') {
      // If late members exist, process them
      if (state.lateMembers.length > 0) {
        const lateMember = state.lateMembers[0];
        setSelectedMember(lateMember);
        actions.selectMember(lateMember);
        setPhase('RESULT');
        actions.setPhase('SPIN_RESULT');
      } else {
        // No more members, go to recap
        actions.setPhase('RECAP');
      }
    }
  }, [remainingMembers.length, phase, state.lateMembers, actions]);

  // Render a reel
  const renderReel = (index: number) => {
    // In RESULT phase, use selectedMember directly (remainingMembers array may have shifted
    // after actions.selectMember removed the member)
    const member = phase === 'RESULT'
      ? selectedMember
      : remainingMembers[reelStates[index]] || selectedMember;

    if (!member) {
      return (
        <div className="reel">
          <div className="reel-avatar empty">?</div>
        </div>
      );
    }

    return (
      <div className={`reel ${phase === 'SPINNING' ? 'spinning' : ''}`}>
        <div
          className="reel-avatar"
          style={{ backgroundColor: getAvatarColor(member.displayName) }}
        >
          {getInitials(member.displayName)}
        </div>
      </div>
    );
  };

  // Jackpot check (all 3 reels show same member)
  const isJackpot = useMemo(() => {
    return phase === 'RESULT' && reelStates[0] === reelStates[1] && reelStates[1] === reelStates[2];
  }, [phase, reelStates]);

  // Generate random coins for animation
  const renderCoins = () => {
    if (!showCoins) return null;
    const coins = [];
    for (let i = 0; i < 15; i++) {
      const left = Math.random() * 100;
      const delay = Math.random() * 0.5;
      coins.push(
        <div
          key={i}
          className="coin"
          style={{
            left: `${left}%`,
            animationDelay: `${delay}s`,
          }}
        />
      );
    }
    return <div className="coins-container">{coins}</div>;
  };

  // Determine slot machine class
  const slotMachineClass = useMemo(() => {
    const classes = ['slot-machine'];
    if (phase === 'SPINNING') classes.push('spinning');
    if (isJackpot) classes.push('winner');
    return classes.join(' ');
  }, [phase, isJackpot]);

  return (
    <div className="screen spin-screen">
      <h2 className="title">WHO'S NEXT?</h2>

      <div className="remaining-count">
        {remainingMembers.length} WARRIORS REMAINING
      </div>

      <div className={slotMachineClass}>
        {/* Main slot area */}
        <div className="slot-main">
          {/* Header with lights */}
          <div className="slot-header">
            <h3 className="slot-title">★ EASY DAILY ★</h3>
            <div className="slot-lights">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="slot-light" />
              ))}
            </div>
          </div>

          {/* Reels */}
          <div className="reels-frame">
            <div className="reels">
              {renderReel(0)}
              {renderReel(1)}
              {renderReel(2)}
            </div>
          </div>

          {/* Spin button integrated */}
          {phase === 'READY' && (
            <button className="slot-spin-button blink" onClick={handleSpin}>
              PULL THE LEVER!
            </button>
          )}

          {phase === 'SPINNING' && (
            <div className="slot-spinning-text">SPINNING...</div>
          )}

          {isJackpot && <div className="jackpot">★ JACKPOT! ★</div>}
          {renderCoins()}
        </div>

        {/* Lever */}
        <div className="slot-lever-container">
          <div className="lever-track">
            <div
              className={`lever-arm ${leverState}`}
              onClick={handleLeverClick}
            >
              <div className="lever-ball" />
              <div className="lever-stick" />
              <div className="lever-base" />
            </div>
          </div>
          <span className="lever-label">PULL</span>
        </div>
      </div>

      {phase === 'RESULT' && selectedMember && (
        <div className="result-panel">
          <h3 className="selected-name">{selectedMember.displayName}</h3>
          <div className="result-issues">
            {selectedMember.issues.length} ISSUES TO PRESENT
          </div>

          <div className="result-actions">
            <button className="action-button action-go" onClick={handleLetsGo}>
              [ENTER] LET'S GO
            </button>
            <button className="action-button action-absent" onClick={handleAbsent}>
              [X] ABSENT
            </button>
            <button className="action-button action-late" onClick={handleLate}>
              [Y] LATE
            </button>
          </div>
        </div>
      )}

      <div className="spin-controls">
        {phase === 'READY' && <span>[SPACE/A] Pull Lever</span>}
        {phase === 'RESULT' && (
          <>
            <span>[ENTER] Go</span>
            <span>[X] Absent</span>
            <span>[Y] Late</span>
          </>
        )}
        <span>[ESC] End Daily</span>
      </div>

      {/* End Daily Button */}
      <button className="end-daily-button" onClick={handleEndDaily}>
        [ESC] END DAILY
      </button>
    </div>
  );
}

export default SpinScreen;
